<template>
  <div>
    <!-- 載入狀態 -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
    </div>

    <!-- 使用者資料與貼文 -->
    <div v-else-if="user" class="space-y-6">
      <!-- 簡化的個人檔案頭部 -->
      <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow">
        <div class="flex items-center space-x-4">
          <!-- 頭像 -->
          <div class="w-16 h-16 rounded-full overflow-hidden bg-wood-100">
            <img 
              :src="user.avatar || '/user.png'" 
              :alt="user.nickname + '的頭像'" 
              class="w-full h-full object-cover"
            />
          </div>
          
          <!-- 基本資訊 -->
          <div class="flex-1">
            <h3 class="text-xl font-bold text-wood-800">{{ user.nickname }}</h3>
            <h5 class="text-wood-600">@{{ user.uid }}</h5>
            <!-- 統計資訊 -->
            <div class="flex items-center space-x-4 mt-2 text-sm text-wood-500">
              <div v-if="stats" class="flex items-center space-x-1">
                <i class="fas fa-calendar-alt"></i>
                <span>{{ stats.accountDays }} 天</span>
              </div>
              <div v-if="stats" class="flex items-center space-x-1">
                <i class="fas fa-file-alt"></i>
                <span>{{ stats.postsCount }} 貼文</span>
              </div>
              <div v-if="stats" class="flex items-center space-x-1">
                <i class="fas fa-users"></i>
                <span>{{ stats.followersCount }} 跟隨者</span>
              </div>
              <div v-if="stats" class="flex items-center space-x-1">
                <i class="fas fa-user-friends"></i>
                <span>{{ stats.followingCount }} 跟隨中</span>
              </div>
            </div>
          </div>
          
          <!-- 跟隨按鈕（非本人時顯示） -->
          <div v-if="!isCurrentUser" class="flex-shrink-0">
            <button 
              @click="toggleFollow"
              :disabled="followLoading"
              :class="[
                'px-4 py-2 rounded-full text-sm transition-all duration-300 font-medium',
                isFollowing 
                  ? 'bg-wood-200 hover:bg-wood-300 text-wood-800 border border-wood-300' 
                  : 'bg-wood-700 hover:bg-wood-800 text-wood-100 shadow-lg'
              ]"
            >
              <i v-if="followLoading" class="fas fa-spinner animate-spin mr-1"></i>
              <i v-else-if="isFollowing" class="fas fa-user-check mr-1"></i>
              <i v-else class="fas fa-user-plus mr-1"></i>
              {{ followLoading ? '處理中' : (isFollowing ? '已跟隨' : '跟隨') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 貼文列表 - 與首頁相同的結構和間距 -->
      <div v-if="posts.length > 0" class="space-y-6">
        <Post 
          v-for="post in posts"
          :key="`user-post-${post.id}`"
          :post="post"
          :isLoggedIn="userStore.isLoggedIn"
          :showDetailOnClick="true"
          @like="handleLike"
          @bookmark="handleBookmark"
          @showMore="handleShowMore"
          @showDetail="showPostDetail"
          @edit="openPostForm"
          @delete="handleDelete"
          @navigateToUser="navigateToUser"
        />

        <!-- 載入更多按鈕 -->
        <div v-if="hasMorePosts" class="flex justify-center pt-6">
          <button 
            @click="loadMorePosts"
            :disabled="loadingPosts"
            class="px-6 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-full transition-all duration-300 shadow-lg"
          >
            <i v-if="loadingPosts" class="fas fa-spinner animate-spin mr-2"></i>
            {{ loadingPosts ? '載入中...' : '載入更多貼文' }}
          </button>
        </div>
      </div>

      <!-- 無貼文狀態 -->
      <div v-else class="text-center py-12">
        <div class="text-wood-400 mb-4">
          <i class="fas fa-file-alt text-5xl"></i>
        </div>
        <p class="text-wood-600 text-lg">{{ user.nickname }} 還沒有發布任何貼文</p>
        <p class="text-wood-500 text-sm mt-2">請稍後再來看看</p>
      </div>
    </div>

    <!-- 使用者不存在 -->
    <div v-else class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow text-center">
      <div class="text-wood-600">
        <i class="fas fa-user-slash text-4xl mb-4 opacity-50"></i>
        <p>找不到此使用者</p>
        <button 
          @click="goBack"
          class="mt-4 px-6 py-2 bg-wood-500 hover:bg-wood-600 text-white rounded-full transition-all duration-300"
        >
          返回
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '../stores/user.js'
import { usePostsStore } from '../stores/posts.js'
import Post from './Post.vue'

const props = defineProps({
  userUid: {
    type: String,
    required: true
  }
})

const userStore = useUserStore()
const postsStore = usePostsStore()

// 狀態變數
const loading = ref(true)
const user = ref(null)
const stats = ref(null)
const posts = ref([])
const isFollowing = ref(false)
const followLoading = ref(false)
const loadingPosts = ref(false)
const hasMorePosts = ref(true)
const fromId = ref(null) // 使用 from 參數進行分頁，類似其他頁面

// 計算屬性
const isCurrentUser = computed(() => {
  return userStore.isLoggedIn && userStore.currentUser?.uid === props.userUid
})

// 載入使用者基本資料
const loadUserData = async () => {
  try {
    const response = await fetch(`/api/v1/users/${props.userUid}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      user.value = data.user
      stats.value = data.stats
      isFollowing.value = data.isFollowing || false
    } else {
      console.error('載入使用者資料失敗:', response.status)
      user.value = null
    }
  } catch (error) {
    console.error('載入使用者資料錯誤:', error)
    user.value = null
  }
}

// 初始化載入
const initializeData = async () => {
  loading.value = true
  
  try {
    // 同時載入使用者資料和貼文
    await Promise.all([
      loadUserData(),
      loadUserPosts()
    ])
  } finally {
    loading.value = false
  }
}

// 載入使用者貼文 - 使用與其他頁面相同的分頁方式
const loadUserPosts = async (append = false) => {
  if (!append) {
    loadingPosts.value = true
  }
  
  try {
    let url = `/users/${props.userUid}/posts.json`
    if (fromId.value && append) {
      url += `?from=${fromId.value}`
    }
    
    console.log(`載入使用者 ${props.userUid} 的貼文:`, url)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      console.log('載入的貼文數量:', data.length)
      
      if (append) {
        posts.value = [...posts.value, ...data]
      } else {
        posts.value = data
      }
      
      // 更新分頁狀態
      if (data.length > 0) {
        fromId.value = Math.min(...data.map(post => post.id))
        hasMorePosts.value = data.length >= 15
      } else {
        hasMorePosts.value = false
      }
    } else {
      console.error('載入使用者貼文失敗:', response.status)
    }
  } catch (error) {
    console.error('載入使用者貼文錯誤:', error)
  } finally {
    if (!append) {
      loadingPosts.value = false
    }
  }
}

// 載入更多貼文
const loadMorePosts = async () => {
  if (!loadingPosts.value && hasMorePosts.value) {
    loadingPosts.value = true
    await loadUserPosts(true)
    loadingPosts.value = false
  }
}

// 切換跟隨狀態
const toggleFollow = async () => {
  if (!userStore.isLoggedIn) {
    userStore.googleLogin()
    return
  }
  
  followLoading.value = true
  
  try {
    const response = await fetch(`/users/${props.userUid}/follow`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
    
    if (response.ok) {
      const wasFollowing = isFollowing.value
      isFollowing.value = !isFollowing.value
      
      // 更新 follower 數量
      if (stats.value) {
        if (wasFollowing) {
          stats.value.followersCount = Math.max(0, stats.value.followersCount - 1)
        } else {
          stats.value.followersCount++
        }
      }
    } else {
      console.error('跟隨操作失敗:', response.status)
    }
  } catch (error) {
    console.error('跟隨操作錯誤:', error)
  } finally {
    followLoading.value = false
  }
}

// 貼文相關操作
const handleLike = async (postId) => {
  try {
    // 直接調用 API
    const response = await fetch(`/posts/${postId}/like`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })

    if (response.ok) {
      const data = await response.json()
      // 更新本地貼文狀態
      const post = posts.value.find(p => p.id === postId)
      if (post) {
        post.isLiked = !post.isLiked
        post.likes = data.likes || (post.isLiked ? post.likes + 1 : post.likes - 1)
      }
    } else {
      console.error('按讚操作失敗:', response.status)
    }
  } catch (error) {
    console.error('按讚操作錯誤:', error)
  }
}

const handleBookmark = async (postId) => {
  try {
    // 直接調用 API
    const response = await fetch(`/posts/${postId}/bookmark`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })

    if (response.ok) {
      // 更新本地貼文狀態
      const post = posts.value.find(p => p.id === postId)
      if (post) {
        post.isBookmarked = !post.isBookmarked
      }
    } else {
      console.error('收藏操作失敗:', response.status)
    }
  } catch (error) {
    console.error('收藏操作錯誤:', error)
  }
}

const handleShowMore = (postId) => {
  // 直接更新本地貼文狀態
  const post = posts.value.find(p => p.id === postId)
  if (post) {
    // 如果 isExpanded 未定義，則初始化為 false
    if (post.isExpanded === undefined) {
      post.isExpanded = false
    }
    
    // 切換展開狀態
    post.isExpanded = !post.isExpanded
  }
}

const handleDelete = async (postId) => {
  if (confirm('您確定要刪除這則貼文嗎？此操作無法復原。')) {
    const success = await postsStore.deletePost(postId)
    if (success) {
      // 從本地列表移除
      posts.value = posts.value.filter(p => p.id !== postId)
    }
  }
}

const showPostDetail = async (postId) => {
  console.log('點擊貼文詳情，ID:', postId)
  
  // 更新 URL
  const url = `/posts/${postId}`
  window.history.pushState(null, '', url)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(url)
}

const openPostForm = (post = null) => {
  // 發出事件讓父組件處理
  console.log('開啟貼文表單:', post)
}

const navigateToUser = (userUid) => {
  console.log('從 UserDetail 導航到使用者頁面，UID:', userUid)
  
  // 更新 URL
  const url = `/users/${userUid}`
  window.history.pushState(null, '', url)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(url)
}

const goBack = () => {
  window.history.back()
}


// 組件掛載時載入資料
onMounted(async () => {
  console.log('UserDetail 組件掛載，使用者 UID:', props.userUid)
  await initializeData()
})

// 監聽 userUid 變化時重新載入資料
watch(() => props.userUid, async (newUid, oldUid) => {
  if (newUid && newUid !== oldUid) {
    // 重置狀態
    posts.value = []
    fromId.value = null
    hasMorePosts.value = true
    await initializeData()
  }
})
</script>

<style scoped>
/* 繼承既有的樣式系統 */
</style>